// What this deployment calls itself (#963): the word drawn in front of the
// brand's name in the document title, so a tab open on staging is not mistaken
// for one open on production.
//
// **The server is asked, and the hostname is never read here.** That is the
// same rule `shell/hostbrand.tsx` states for the brand, for a stronger reason:
// a page cannot check what a host means, and after #944 staging's console and
// production's differ by one label — `console.arpiq.dev` against
// `console.arpiq.net` — which is exactly the shape a client-side guess gets
// wrong. What a deployment is called is a fact the process holds about itself
// (ZENITH.md §25), so the process is what says it.
//
// **One read for both session states**, which is why this is not folded into
// either brand path. `GET /brand` answers before a session and `GET /me`
// carries the brand after one, so a label living on those would be two fields
// with one meaning and two chances to drift. The marker is the same word on
// every page of a deployment, signed in or not, so it is read once from a path
// that answers in both states.
import { useEffect, useState } from "react";
import { api } from "../api/client";

/** The label, once the server has answered. Empty is the answer production
 * gives, not a value still outstanding — the two are indistinguishable here on
 * purpose, because both render the same thing: the brand's name alone. */
export type DeploymentLabel = string;

/** The read, shared by every caller in this document.
 *
 * A page load asks once: the label cannot change without a deploy, and a
 * deploy replaces the document. Without this the sign-in page would ask, and
 * the shell would ask again the moment somebody signed in, for a value that
 * was the same both times — the per-request cost `hostbrand.tsx` weighed when
 * it chose a hook over a provider, arriving here through a different door.
 *
 * A console window is a browsing context of its own, so it holds its own copy
 * of this module and asks once itself. That is correct rather than wasteful:
 * it may outlive the window that opened it. */
let pending: Promise<string> | undefined;

function read(): Promise<string> {
  pending ??= api
    .GET("/deployment")
    .then(({ data }) => data?.environment_label ?? "")
    // A deployment that cannot be read is drawn as production rather than as
    // an error: the title is not a surface to report a failed request on, and
    // an unmarked title is the one reading that is never *wrong* about which
    // deployment this is — it only fails to say.
    .catch(() => "");
  return pending;
}

/** Forgets the cached read. For tests, which mount many documents in one
 * module instance; a browser never needs it. */
export function forgetDeployment(): void {
  pending = undefined;
}

/** The label this deployment wears, empty until the server has answered. */
export function useDeploymentLabel(): DeploymentLabel {
  const [label, setLabel] = useState("");
  useEffect(() => {
    let live = true;
    void read().then((l) => {
      if (live) setLabel(l);
    });
    return () => {
      live = false;
    };
  }, []);
  return label;
}

/** The document title: the marker, then the name the page would have drawn
 * anyway. One composer for both writers — `applyBrand` and the console
 * window — so a deployment cannot be marked on one and not the other.
 *
 * Empty label gives the name unchanged, which is production and is also what
 * every page renders before the read comes back. */
export function titleWith(label: DeploymentLabel, name: string): string {
  return label ? `${label} ${name}` : name;
}
